export type Company = {
  id: string;
  lassoId: string;
  creationDate: string | null;
  employees: {
    count?: number;
    interval: string | null;
    month: number;
    year: number;
    type: "Monthly" | "Quarterly" | "Yearly";
  } | null;
  cvr: number;
  name: string;
  address: Address;
  commerciallyProtected: boolean;
  phone: string | null;
  email: string | null;
  website: string | null;
  fax: string | null;
  purpose: string | null;
  industry: Industry;
  board: {
    chairman: Participant | null;
    members: Participant[];
    alternates: Participant[];
  };
  trueOwners: unknown;
  ownership: {
    hasOwnersUnderFivePercent: boolean;
    owners: Participant[];
  };
  founders: unknown;
  management: {
    ceo: Participant | null;
    members: Participant[];
  };
  stakeholders: Participant[];
};

export type Address = {
  address1: string;
  address2: string | null;
  streetName: string;
  houseNumberFrom: number;
  houseNumberTo: number | null;
  houseLetterFrom: string;
  houseLetterTo: string | null;
  floor: string | null;
  sideDoor: string | null;
  postalCode: number;
  postalDistrict: string | null;
  cityName: string | null;
  municipality: {
    code: number;
    name: string | null;
  };
  coName: string | null;
  postalBox: string | null;
  freeText: string | null;
  countryCode: string;
};

export type Management = object;

export type Participant = {
  address: Address;
  name: string;
  type: string;
  lassoId: string;
  unitNumber: number | null;
  cvr: number | null;
  role?: Role;
  from: string | null;
};

export type Role = {
  mainType: string;
  type: string;
  originalType: string;
  attributes: Attribute[];
};

export interface Attribute {
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any; // marked as `object` in the API
}

export type Industry = {
  code: string;
  text: string;
};

export type CompanyResume = {
  lassoId: string;
  content: string;
};

export type Observation = {
  title: string;
  type: string;
  tags: string[];
  isObserved: boolean;
  observationDescription?: string;
  shortDescription: string | null;
  description: string | null;
  outcome: number;
  notAvailable: boolean;
  errors: null;
  relatedLassoId: string;
};

export type ObservationResponse = {
  observations: Observation[];
  percentages: unknown;
  relatedCompanyName: string | null;
  relatedLassoId: string | null;
  relatedName: string | null;
  relatedObservations: Observation[];
  relatedPersonName: string | null;
  score: number;
  version: string;
};

export interface VIRKReportBase {
  cvr: number;
  reportType: ReportType;
  reportYear: number | null;
  period: VIRKAccountingPeriod;
  publicationTime: string | null;
  reportUrl: string;
  xBRLUrl: string;
  documents: unknown[];
}

export interface VIRKReport extends VIRKReportBase {
  groupReport: VIRKReport;
  phrases: PhraseOccurence[];
  currencies: string[];
  convertedFromCurrencies: unknown[];
  administrativeExpenses: VIRKFinancialDecimalField | null;
  assets: VIRKFinancialDecimalField | null;
  auditors: Auditor[];
  auditorSignatureDate: VIRKFinancialDateField | null;
  bankCVR: VIRKFinancialIntegerField | null;
  bankName: VIRKFinancialStringField | null;
  capital: VIRKFinancialDecimalField | null;
  cashHoldings: VIRKFinancialDecimalField | null;
  classOfReportingEntity: VIRKFinancialStringField | null;
  completedDevelopmentProjects: VIRKFinancialDecimalField | null;
  costOfProduction: VIRKFinancialDecimalField | null;
  coverage: VIRKFinancialDecimalField | null;
  currentAssets: VIRKFinancialDecimalField | null;
  debt: VIRKFinancialDecimalField | null;
  deferredIncomeAssets: VIRKFinancialDecimalField | null;
  deferredTaxAssets: VIRKFinancialDecimalField | null;
  depreciations: VIRKFinancialDecimalField | null;
  descriptionOfPrimaryActivities: VIRKFinancialStringField | null;
  descriptionOfQualifications: VIRKFinancialStringField | null;
  developmentProjectsInProgressAndPrepaymentsForIntangibleAssets: VIRKFinancialDecimalField | null;
  distributionCosts: VIRKFinancialDecimalField | null;
  dividend: VIRKFinancialDecimalField | null;
  ebitda: VIRKFinancialDecimalField | null;
  employeeExpenses: VIRKFinancialDecimalField | null;
  employees: VIRKFinancialDecimalField | null;
  equityExcludingDividend: VIRKFinancialDecimalField | null;
  equity: VIRKFinancialDecimalField | null;
  equityLoan: VIRKFinancialDecimalField | null;
  equityReturn: VIRKFinancialDecimalField | null;
  externalExpenses: VIRKFinancialDecimalField | null;
  extraordinaryExpenses: VIRKFinancialDecimalField | null;
  financialFixedAssets: VIRKFinancialDecimalField | null;
  fixturesFittingsToolsAndEquipment: VIRKFinancialDecimalField | null;
  goingConcern: VIRKFinancialStringField | null;
  goodwill: VIRKFinancialDecimalField | null;
  grossResult: VIRKFinancialDecimalField | null;
  intangibleFixedAssets: VIRKFinancialDecimalField | null;
  inventories: VIRKFinancialDecimalField | null;
  landAndBuildings: VIRKFinancialDecimalField | null;
  liabilitiesAndEquity: VIRKFinancialDecimalField | null;
  liquidityRatio: VIRKFinancialDecimalField | null;
  longtermDebtToBanks: VIRKFinancialDecimalField | null;
  longtermDebtToCreditInstitutions: VIRKFinancialDecimalField | null;
  longtermDeferredIncome: VIRKFinancialDecimalField | null;
  longtermEquityLoan: VIRKFinancialDecimalField | null;
  longtermInvestmentsInAssociates: VIRKFinancialDecimalField | null;
  longtermInvestmentsInGroupEnterprises: VIRKFinancialDecimalField | null;
  longtermLiabilities: VIRKFinancialDecimalField | null;
  longtermMortgageDebt: VIRKFinancialDecimalField | null;
  longtermParticipatingInterests: VIRKFinancialDecimalField | null;
  longtermPayablesToGroupEnterprises: VIRKFinancialDecimalField | null;
  longtermPayablesToParticipatingInterests: VIRKFinancialDecimalField | null;
  longtermPrepaymentsReceivedFromCustomers: VIRKFinancialDecimalField | null;
  longtermReceivablesFromGroupEnterprises: VIRKFinancialDecimalField | null;
  longtermReceivablesFromOwnersAndManagement: VIRKFinancialDecimalField | null;
  longtermReceivablesFromParticipatingInterests: VIRKFinancialDecimalField | null;
  managementsReview: VIRKFinancialStringField | null;
  minorityInterests: VIRKFinancialDecimalField | null;
  nonCurrentAssets: VIRKFinancialDecimalField | null;
  operatingMargin: VIRKFinancialDecimalField | null;
  opinionOnFinancialStatements: VIRKFinancialStringField | null;
  otherFinanceExpenses: VIRKFinancialDecimalField | null;
  otherFinanceIncome: VIRKFinancialDecimalField | null;
  otherFinanceIncomeFromGroupEnterprises: VIRKFinancialDecimalField | null;
  otherLongtermInvestments: VIRKFinancialDecimalField | null;
  otherLongtermReceivables: VIRKFinancialDecimalField | null;
  otherShorttermInvestments: VIRKFinancialDecimalField | null;
  otherShorttermReceivables: VIRKFinancialDecimalField | null;
  otherOperatingIncome: VIRKFinancialDecimalField | null;
  otherReserves: VIRKFinancialDecimalField | null;
  pensions: VIRKFinancialDecimalField | null;
  plantAndMachinery: VIRKFinancialDecimalField | null;
  profitLossFromAssociates: VIRKFinancialDecimalField | null;
  profitFromOrdinaryOperatingActivities: VIRKFinancialDecimalField | null;
  profitLossFromGroupEnterprises: VIRKFinancialDecimalField | null;
  propertyCost: VIRKFinancialDecimalField | null;
  propertyPlantAndEquipmentInProgressAndPrepaymentsForPropertyPlantAndEquipment: VIRKFinancialDecimalField | null;
  provisions: VIRKFinancialDecimalField | null;
  provisionsForDeferredTax: VIRKFinancialDecimalField | null;
  reportingCompanyName: VIRKFinancialStringField | null;
  reserveForDevelopmentExpenditure: VIRKFinancialDecimalField | null;
  result: VIRKFinancialDecimalField | null;
  resultBeforeTax: VIRKFinancialDecimalField | null;
  retainedEarnings: VIRKFinancialDecimalField | null;
  revenue: VIRKFinancialDecimalField | null;
  roi: VIRKFinancialDecimalField | null;
  salaries: VIRKFinancialDecimalField | null;
  shorttermDebtToBanks: VIRKFinancialDecimalField | null;
  shorttermDeferredIncome: VIRKFinancialDecimalField | null;
  shorttermInvestments: VIRKFinancialDecimalField | null;
  shorttermLiabilities: VIRKFinancialDecimalField | null;
  shorttermMortgageDebt: VIRKFinancialDecimalField | null;
  shorttermParticipatingInterests: VIRKFinancialDecimalField | null;
  shorttermPartOfLongtermLiabilitiesOtherThanProvisions: VIRKFinancialDecimalField | null;
  shorttermPayablesToGroupEnterprises: VIRKFinancialDecimalField | null;
  shorttermPayablesToParticipatingInterest: VIRKFinancialDecimalField | null;
  shorttermPrepaymentsReceivedFromCustomers: VIRKFinancialDecimalField | null;
  shorttermReceivables: VIRKFinancialDecimalField | null;
  shorttermReceivablesFromGroupEnterprises: VIRKFinancialDecimalField | null;
  shorttermReceivablesFromOwnersAndManagement: VIRKFinancialDecimalField | null;
  shorttermReceivablesFromParticipatingInterests: VIRKFinancialDecimalField | null;
  shorttermTaxPayables: VIRKFinancialDecimalField | null;
  shorttermTaxPayablesToGroupEnterprises: VIRKFinancialDecimalField | null;
  shorttermTaxReceivablesFromGroupEnterprises: VIRKFinancialDecimalField | null;
  shorttermTradeReceivables: VIRKFinancialDecimalField | null;
  shorttermTradePayables: VIRKFinancialDecimalField | null;
  socialSecurityExpenses: VIRKFinancialDecimalField | null;
  solvencyRatio: VIRKFinancialDecimalField | null;
  solvencyRatioExcludingDividend: VIRKFinancialDecimalField | null;
  submittingCompanyCVR: VIRKFinancialIntegerField | null;
  submittingCompanyName: VIRKFinancialStringField | null;
  supplementaryInformation: VIRKFinancialStringField[] | null;
  tangibleFixedAssets: VIRKFinancialDecimalField | null;
  taxExpense: VIRKFinancialDecimalField | null;
  typeOfAuditorAssistance: VIRKFinancialStringField | null;
  typeOfBasisForModifiedOpinion: VIRKFinancialStringField | null;
  additionalFields: { [key: string]: VIRKFinancialField };
}

export interface Auditor {
  companyName: string;
  companyCVR: number | null;
  id: string;
  name: string;
  title: string;
  street: string;
  postalCode: string;
  description: string;
}

export interface PhraseOccurence {
  phrase: string;
  occurences: string[];
}

export enum ReportType {
  Yearly = "Yearly",
  Quarterly = "Quarterly",
  HalfYearly = "HalfYearly",
  Other = "Other",
  Yearly_ESEF = "Yearly_ESEF",
}

export enum TypeOfAuditorAssistanceEnum {
  IngenErklaering = "IngenErklaering",
  Revision = "Revision",
  UdvidetGennemgang = "UdvidetGennemgang",
  Review = "Review",
  AndreErklaering = "AndreErklaering",
  Other = "Other",
}

export enum TypeOfAuditorAssistanceConvertedEnum {
  IngenErklaering = 0,
  Revision = 1,
  UdvidetGennemgang = 2,
  Review = 3,
  AndreErklaering = 4,
  Other = 5,
}

export enum SupplementaryInfoType {
  SupplementaryInformationAuditorsReview = "SupplementaryInformationAuditorsReview",
  SupplementaryInformation = "SupplementaryInformation",
  AuditorsReportsAccordingToOtherLegislationAndRegulationReview = "AuditorsReportsAccordingToOtherLegislationAndRegulationReview",
  OtherReportsAuditorsReport = "OtherReportsAuditorsReport",
  SupplementaryInformationOnExtendedReview = "SupplementaryInformationOnExtendedReview",
  MaterialUncertaintyConcerningGoingConcernAudit = "MaterialUncertaintyConcerningGoingConcernAudit",
  SupplementaryInformationOnAudit = "SupplementaryInformationOnAudit",
  OtherReportingResponsibilitiesAudit = "OtherReportingResponsibilitiesAudit",
}

export interface VIRKAccountingPeriod {
  from: string | null;
  to: string | null;
  instant: string | null;
}

export interface VIRKFinancialField {
  sources: string[];
  possibleError: boolean;
  error: boolean;
}

export interface VIRKFinancialIntegerField extends VIRKFinancialField {
  unit: string;
  value: number;
}

export interface VIRKFinancialDecimalField extends VIRKFinancialField {
  unit: string;
  value: number;
  calculation: string;
}

export interface VIRKFinancialDateField extends VIRKFinancialField {
  value: string | null;
}

export interface VIRKFinancialStringField extends VIRKFinancialField {
  value: string;
}
